// Iter79 — Defect breakdown popup for sorting result entries.
// Captures defect-type → quantity rows; sums against the NOK + rework totals.
// Reused for both initial entry and editing already-saved rows.
import React, { useMemo, useState } from "react";

const DEFAULT_TYPES = [
  "scratch", "dent", "missing", "wrong_part", "dimensional",
  "weld", "assembly_error", "contamination", "label_missing", "other",
];

const inputStyle = {
  background: "var(--bg3, #08090d)",
  border: "1px solid rgba(255,255,255,0.10)",
  color: "var(--text, #f0f2f8)",
  fontFamily: "DM Mono, monospace", fontSize: 12,
  width: "100%", padding: "5px 7px", borderRadius: 4, outline: "none",
};

const mono = (size, color = "#9198b0", weight = 400, ls = "1.2px") => ({
  fontFamily: "DM Mono, monospace", fontSize: size, color, fontWeight: weight, letterSpacing: ls,
});

const toInt = (v) => {
  const n = parseInt(v || 0, 10);
  return Number.isFinite(n) ? n : 0;
};

export const DefectBreakdownModal = ({
  initial = {},
  nokQty = 0,
  reworkQty = 0,
  knownTypes = DEFAULT_TYPES,
  rowLabel = "",
  onClose,
  onSave,
}) => {
  const seed = useMemo(() => {
    const rows = Object.entries(initial || {})
      .filter(([k]) => k)
      .map(([type, qty]) => ({ type, qty: String(qty || "") }));
    return rows.length ? rows : [{ type: "", qty: "" }];
  }, [initial]);

  const [rows, setRows] = useState(seed);
  const [customType, setCustomType] = useState("");
  const [error, setError] = useState("");

  const updateRow = (i, patch) => setRows((p) => p.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const addRow = (preset = "") => setRows((p) => [...p, { type: preset, qty: "" }]);
  const removeRow = (i) => setRows((p) => p.length > 1 ? p.filter((_, idx) => idx !== i) : p);

  const totalReported = rows.reduce((s, r) => s + toInt(r.qty), 0);
  const target = toInt(nokQty) + toInt(reworkQty);
  const mismatch = target > 0 && totalReported !== target;

  const save = () => {
    setError("");
    const map = {};
    for (const r of rows) {
      const t = (r.type || "").trim();
      const q = toInt(r.qty);
      if (!t && q === 0) continue;
      if (!t) { setError(`Row ${rows.indexOf(r) + 1}: defect type is required`); return; }
      if (q <= 0) { setError(`Row ${rows.indexOf(r) + 1}: quantity must be > 0`); return; }
      if (map[t]) { setError(`Duplicate defect type: ${t}`); return; }
      map[t] = q;
    }
    onSave?.(map);
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center px-3"
      style={{ background: "rgba(0,0,0,0.65)", zIndex: 500 }} onClick={onClose}
      data-testid="defect-breakdown-overlay">
      <div className="rounded-md p-4"
        style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.18)", width: "min(560px, 96vw)", maxHeight: "92vh", overflowY: "auto" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="defect-breakdown-modal">
        <div className="flex items-center justify-between mb-2">
          <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 15, color: "var(--text, #f0f2f8)" }}>
            Defect breakdown {rowLabel ? `· ${rowLabel}` : ""}
          </span>
          <button onClick={onClose} data-testid="defect-breakdown-close" style={{ color: "#9aa1b3", fontSize: 22, cursor: "pointer", background: "transparent", border: "none" }}>×</button>
        </div>

        <div className="mb-2 p-2 rounded" style={{ background: "rgba(155,89,182,0.06)", border: "1px solid rgba(155,89,182,0.20)" }}>
          <span style={mono(10, "#c89adb", 700, "0.4px")}>Target = NOK ({toInt(nokQty)}) + Rework ({toInt(reworkQty)}) = {target}</span>
          {target > 0 && (
            <span className="ml-2" style={mono(10, mismatch ? "#ff9a99" : "#9aebc2", 700, "0.4px")}>
              · Reported: {totalReported}
            </span>
          )}
        </div>

        <div className="rounded overflow-hidden" style={{ border: "1px solid rgba(255,255,255,0.10)" }}>
          <div className="grid grid-cols-12 gap-2 px-3 py-2" style={{ background: "rgba(255,255,255,0.04)" }}>
            <div className="col-span-7" style={mono(9, undefined, 600)}>DEFECT TYPE</div>
            <div className="col-span-4" style={mono(9, undefined, 600)}>QTY</div>
            <div className="col-span-1" />
          </div>
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 px-3 py-1.5 items-center" style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }} data-testid={`defect-breakdown-row-${i}`}>
              <div className="col-span-7">
                <input list="defect-types-list" type="text" value={r.type} onChange={(e) => updateRow(i, { type: e.target.value })}
                  style={inputStyle} placeholder="e.g. scratch" data-testid={`defect-breakdown-type-${i}`} />
              </div>
              <div className="col-span-4">
                <input type="number" min="0" value={r.qty} onChange={(e) => updateRow(i, { qty: e.target.value })}
                  style={{ ...inputStyle, textAlign: "right" }} data-testid={`defect-breakdown-qty-${i}`} />
              </div>
              <div className="col-span-1 flex justify-end">
                <button type="button" onClick={() => removeRow(i)} disabled={rows.length <= 1}
                  style={{ color: rows.length <= 1 ? "#3a3f55" : "#ff9a99", cursor: rows.length <= 1 ? "not-allowed" : "pointer", fontSize: 14, background: "transparent", border: "none" }}
                  data-testid={`defect-breakdown-remove-${i}`}>✕</button>
              </div>
            </div>
          ))}
        </div>
        <datalist id="defect-types-list">
          {knownTypes.map((t) => <option key={t} value={t} />)}
        </datalist>

        <div className="mt-2 flex items-center gap-2">
          <button type="button" onClick={() => addRow()}
            className="px-2.5 py-1 rounded"
            style={{ background: "rgba(24,95,165,0.16)", border: "1px solid rgba(24,95,165,0.55)", color: "#9ec3ec", ...mono(10, undefined, 700, "0.4px"), cursor: "pointer" }}
            data-testid="defect-breakdown-add-row">＋ Add defect</button>
          <input type="text" value={customType} onChange={(e) => setCustomType(e.target.value)}
            placeholder="Quick add type…" style={{ ...inputStyle, maxWidth: 200 }} data-testid="defect-breakdown-custom-type" />
          <button type="button" disabled={!customType.trim()} onClick={() => { addRow(customType.trim()); setCustomType(""); }}
            className="px-2.5 py-1 rounded"
            style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.14)", color: "#c5cad8", ...mono(10), cursor: customType.trim() ? "pointer" : "not-allowed", opacity: customType.trim() ? 1 : 0.5 }}
            data-testid="defect-breakdown-add-custom">＋ Add custom</button>
        </div>

        {error && <div className="mt-3 p-2 rounded" style={{ background: "rgba(226,75,74,0.08)", border: "1px solid rgba(226,75,74,0.35)", color: "#ff9a99", ...mono(11) }} data-testid="defect-breakdown-error">{error}</div>}

        <div className="mt-3 flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="px-3 py-1.5 rounded"
            style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.14)", color: "#c5cad8", fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 12, cursor: "pointer" }}
            data-testid="defect-breakdown-cancel">Cancel</button>
          <button type="button" onClick={save}
            className="px-3 py-1.5 rounded"
            style={{ background: "#185FA5", color: "#fff", fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 12, cursor: "pointer", border: "none" }}
            data-testid="defect-breakdown-save">Save breakdown</button>
        </div>
      </div>
    </div>
  );
};
